import { faPlay, faSync, faTrash } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import {
    ACTIVATE_ENTRY,
    BZ2_GET_PLAN,
    DELETE_ENTRY,
    DELETED_REASON_ANOTHER_BAZAAR_MESSAGE,
    DELETED_REASON_COPYRIGHT_PHOTOS_MESSAGE,
    DELETED_REASON_DONT_KNOW_WHAT_SELLING_MESSAGE,
    DELETED_REASON_INVISIBLE_PRODUCT_MESSAGE,
    DELETED_REASON_LANGUAGE_MESSAGE,
    DELETED_REASON_MULTIPLE_PRODUCTS_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_ANNOUNCEMENT_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_CONTEXT_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_DRUGS_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_DUPLICATE_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_INQUIRY_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_MESSAGE,
    DELETED_REASON_NOT_ALLOWED_OPEN_MESSAGE,
    DELETED_REASON_NOT_FOR_TWINS_MESSAGE,
    DELETED_REASON_SERVICE_MESSAGE,
    DELETED_REASON_WRONG_PRICE_MESSAGE,
    REPOST_ENTRY,
    THE_ENTRY_DOES_NOT_EXIST,
    THE_ENTRY_WAS_DELETED_ON_date,
    THE_PRODUCT_IS_EXPIRED,
    THE_PRODUCT_IS_HIDDEN,
    THE_PRODUCT_IS_INACTIVE,
    YOU_HAVE_NO_MORE_REPOSTS_SHORT,
} from 'mk/autogenerated/translations/ProductState.bbcf4348996bcac98759302a78e66f3c'
import { DeletedReason, ProductEntity } from 'mk2/apps/bazaar/schemas';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { planChoosePlanUrl } from 'mk2/apps/plan/urls';
import { Btn, BtnType } from 'mk2/components/Btn';
import { Dot } from 'mk2/components/Dot';
import { Link } from 'mk2/components/Link';
import { PlusProBadge } from 'mk2/components/PlusProBadge';
import { timeAt } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import styles from './ProductState.mscss';

interface OwnProps {
    className?: string;
    product: ProductEntity;
    isSeller?: boolean;
    isAdmin?: boolean;
    isActivating?: boolean;
    isDeleting?: boolean;
    isReposting?: boolean;

    onDelete();
    onRepost();
    onActivate();
}

type Props = OwnProps;

const sellerMessages = {
    [DeletedReason.DELETED_REASON_COPYRIGHT_PHOTOS]: DELETED_REASON_COPYRIGHT_PHOTOS_MESSAGE,
    [DeletedReason.DELETED_REASON_WRONG_PRICE]: DELETED_REASON_WRONG_PRICE_MESSAGE,
    [DeletedReason.DELETED_REASON_INVISIBLE_PRODUCT]: DELETED_REASON_INVISIBLE_PRODUCT_MESSAGE,
    [DeletedReason.DELETED_REASON_DONT_KNOW_WHAT_SELLING]: DELETED_REASON_DONT_KNOW_WHAT_SELLING_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_FOR_TWINS]: DELETED_REASON_NOT_FOR_TWINS_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED]: DELETED_REASON_NOT_ALLOWED_MESSAGE,
    [DeletedReason.DELETED_REASON_MULTIPLE_PRODUCTS]: DELETED_REASON_MULTIPLE_PRODUCTS_MESSAGE,
    [DeletedReason.DELETED_REASON_SERVICE]: DELETED_REASON_SERVICE_MESSAGE,
    [DeletedReason.DELETED_REASON_LANGUAGE]: DELETED_REASON_LANGUAGE_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_DUPLICATE]: DELETED_REASON_NOT_ALLOWED_DUPLICATE_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_DRUGS]: DELETED_REASON_NOT_ALLOWED_DRUGS_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_OPEN]: DELETED_REASON_NOT_ALLOWED_OPEN_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_ANNOUNCEMENT]: DELETED_REASON_NOT_ALLOWED_ANNOUNCEMENT_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_INQUIRY]: DELETED_REASON_NOT_ALLOWED_INQUIRY_MESSAGE,
    [DeletedReason.DELETED_REASON_NOT_ALLOWED_CONTEXT]: DELETED_REASON_NOT_ALLOWED_CONTEXT_MESSAGE,
    [DeletedReason.DELETED_REASON_ANOTHER_BAZAAR]: DELETED_REASON_ANOTHER_BAZAAR_MESSAGE,
};

export const ProductState: React.FunctionComponent<Props> = React.memo(({
    className,
    product,
    isSeller = false,
    isAdmin = false,
    onActivate,
    onDelete,
    isActivating = false,
    isDeleting = false,
    isReposting = false,
    onRepost,
}) => {
    const category = product.properties.find((p) => p[0] === 'cat')[1];

    const onDeleteCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            onDelete();
        },
        [onDelete],
    );

    if (product.state.isDeleted) {
        const showDeleteReason = product.deletedReason && (isSeller || isAdmin);
        const showDeletedBy = product.deletedBy && isAdmin;

        return (
            <div className={cx(styles.ProductState, className, showDeleteReason && styles['ProductState--withReason'])}>
                {showDeleteReason ? (
                    <>
                        <span dangerouslySetInnerHTML={{ __html: sellerMessages[product.deletedReason] }} />
                        {showDeletedBy && (
                            <div className={styles.ProductState__adminNote}>
                                <Link to={url(blogsProfileUrl, { username: product.deletedBy })} target="_blank">
                                    {product.deletedBy}
                                </Link>
                                <Dot />
                                {interpolate(THE_ENTRY_WAS_DELETED_ON_date, { date: timeAt(product.endTime) })}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <strong className={styles.ProductState__title}>{THE_ENTRY_DOES_NOT_EXIST}</strong>
                        {interpolate(THE_ENTRY_WAS_DELETED_ON_date, { date: timeAt(product.endTime) })}
                    </>
                )}
            </div>
        );
    } else if (product.state.isHidden && (isSeller || isAdmin)) {
        return (
            <div className={cx(styles.ProductState, className)}>
                <strong className={styles.ProductState__title}>{THE_PRODUCT_IS_HIDDEN}</strong>
                <div className={styles.ProductState__actions}>
                    <Link
                        className={styles.ProductState__actions__action}
                        to={url(planChoosePlanUrl, {}, { src: 'detailOverlimit' })}
                    >
                        {BZ2_GET_PLAN}&nbsp;
                        <PlusProBadge />
                    </Link>
                </div>
            </div>
        );
    } else if (product.state.isInactive) {
        return (
            <div className={cx(styles.ProductState, className)}>
                <strong className={styles.ProductState__title}>{THE_PRODUCT_IS_INACTIVE}</strong>
                {isSeller && !product.seller.hasBlockedBazaar && (
                    <div className={styles.ProductState__actions}>
                        <Btn
                            className={styles.ProductState__actions__action}
                            type={BtnType.BlueOutline}
                            icon={faPlay}
                            label={ACTIVATE_ENTRY}
                            onClick={onActivate}
                            hasSpinner={isActivating}
                        />
                    </div>
                )}
            </div>
        );
    } else if (product.state.isExpired) {
        return (
            <div className={cx(styles.ProductState, className)}>
                <strong className={styles.ProductState__title}>{THE_PRODUCT_IS_EXPIRED}</strong>
                {isSeller && !product.seller.hasBlockedBazaar && (
                    <div className={styles.ProductState__actions}>
                        <Btn
                            className={styles.ProductState__actions__action}
                            type={BtnType.Blue}
                            icon={faSync}
                            label={product.seller.availableReposts ? REPOST_ENTRY : YOU_HAVE_NO_MORE_REPOSTS_SHORT}
                            onClick={onRepost}
                            hasSpinner={isReposting}
                            disabled={!product.seller.availableReposts}
                        />
                        <Btn
                            type={BtnType.BlueOutline}
                            icon={faTrash}
                            label={DELETE_ENTRY}
                            onClick={onDeleteCallback}
                            className={styles.ProductState__actions__action}
                            hasSpinner={isDeleting}
                        />
                    </div>
                )}
            </div>
        );
    }

    return null;
});

ProductState.displayName = 'ProductState';
